import React, { useState, useEffect } from 'react';
import { getGamingAnalyticsDashboard, getRealTimeMetrics, getCohortAnalysis } from '../../../utils/gamingAnalyticsService';
import Icon from '../../../components/AppIcon';
import { 
  Users, 
  GamepadIcon, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  Target,
  Play,
  Star,
  Activity,
  BarChart3,
  ExternalLink,
  Zap,
  Trophy,
  UserCheck,
  Calendar
} from 'lucide-react';

const GamingAnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [realTimeData, setRealTimeData] = useState(null);
  const [cohortData, setCohortData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');

  useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setLoading(true);
        
        const [analytics, realTime, cohorts] = await Promise.all([
          getGamingAnalyticsDashboard(),
          getRealTimeMetrics(),
          getCohortAnalysis()
        ]);
        
        setAnalyticsData(analytics);
        setRealTimeData(realTime);
        setCohortData(cohorts);
        
      } catch (err) {
        console.error('Error loading gaming analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    loadAnalyticsData();
    
    // Set up real-time updates every 30 seconds
    const interval = setInterval(async () => {
      try {
        const realTime = await getRealTimeMetrics();
        setRealTimeData(realTime);
      } catch (err) {
        console.error('Error updating real-time data:', err);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-muted rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !analyticsData) {
    return (
      <div className="bg-card border border-destructive rounded-lg p-6">
        <div className="text-center">
          <Icon name="Activity" size={48} className="text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-destructive mb-2">Analytics Error</h3>
          <p className="text-muted-foreground">{error || 'Failed to load analytics data'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with PostHog Link */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Gaming Analytics Dashboard</h2>
          <p className="text-muted-foreground">Netflix-style analytics for your gaming platform</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select 
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <a
            href="https://app.posthog.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View in PostHog
          </a>
        </div>
      </div>

      {/* Real-time Metrics */}
      {realTimeData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-card border border-green-500/20 bg-green-500/5 rounded-lg p-6">
            <div className="flex items-center text-green-400 mb-2">
              <Zap className="w-4 h-4 mr-2" />
              <h4 className="text-sm font-medium">Live Users</h4>
            </div>
            <div className="text-2xl font-bold text-green-400">{realTimeData.activeUsers}</div>
            <p className="text-xs text-muted-foreground">Currently online</p>
          </div>

          <div className="bg-card border border-blue-500/20 bg-blue-500/5 rounded-lg p-6">
            <div className="flex items-center text-blue-400 mb-2">
              <Play className="w-4 h-4 mr-2" />
              <h4 className="text-sm font-medium">Active Games</h4>
            </div>
            <div className="text-2xl font-bold text-blue-400">{realTimeData.activeGames}</div>
            <p className="text-xs text-muted-foreground">Being played now</p>
          </div>

          <div className="bg-card border border-purple-500/20 bg-purple-500/5 rounded-lg p-6">
            <div className="flex items-center text-purple-400 mb-2">
              <DollarSign className="w-4 h-4 mr-2" />
              <h4 className="text-sm font-medium">Revenue Today</h4>
            </div>
            <div className="text-2xl font-bold text-purple-400">${realTimeData.revenueToday}</div>
            <p className="text-xs text-muted-foreground">Daily earnings</p>
          </div>

          <div className="bg-card border border-orange-500/20 bg-orange-500/5 rounded-lg p-6">
            <div className="flex items-center text-orange-400 mb-2">
              <UserCheck className="w-4 h-4 mr-2" />
              <h4 className="text-sm font-medium">New Signups</h4>
            </div>
            <div className="text-2xl font-bold text-orange-400">{realTimeData.newSignups}</div>
            <p className="text-xs text-muted-foreground">Today</p>
          </div>

          <div className="bg-card border border-red-500/20 bg-red-500/5 rounded-lg p-6">
            <div className="flex items-center text-red-400 mb-2">
              <Activity className="w-4 h-4 mr-2" />
              <h4 className="text-sm font-medium">Server Load</h4>
            </div>
            <div className="text-2xl font-bold text-red-400">{realTimeData.currentLoad}%</div>
            <p className="text-xs text-muted-foreground">Current usage</p>
          </div>
        </div>
      )}

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center mb-2">
            <Users className="w-4 h-4 mr-2" />
            <h4 className="text-sm font-medium">Total Users</h4>
          </div>
          <div className="text-2xl font-bold">{analyticsData.overview.totalUsers.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {analyticsData.overview.activeUsers} active users
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center mb-2">
            <GamepadIcon className="w-4 h-4 mr-2" />
            <h4 className="text-sm font-medium">Total Games</h4>
          </div>
          <div className="text-2xl font-bold">{analyticsData.overview.totalGames}</div>
          <p className="text-xs text-muted-foreground">
            Available in library
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center mb-2">
            <DollarSign className="w-4 h-4 mr-2" />
            <h4 className="text-sm font-medium">Monthly Revenue</h4>
          </div>
          <div className="text-2xl font-bold">${analyticsData.overview.monthlyRevenue.toFixed(2)}</div>
          <p className="text-xs text-muted-foreground">
            {analyticsData.overview.activeSubscriptions} active subscriptions
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center mb-2">
            <TrendingUp className="w-4 h-4 mr-2" />
            <h4 className="text-sm font-medium">Conversion Rate</h4>
          </div>
          <div className="text-2xl font-bold">{analyticsData.overview.conversionRate}%</div>
          <p className="text-xs text-muted-foreground">
            Free to paid conversion
          </p>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Activity className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">User Engagement</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Avg Session Duration</span>
              <span className="font-semibold">{analyticsData.engagement.avgSessionDuration}m</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Sessions per User</span>
              <span className="font-semibold">{analyticsData.engagement.avgSessionsPerUser}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Daily Active Users</span>
              <span className="font-semibold">{analyticsData.engagement.dailyActiveUsers}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Weekly Active Users</span>
              <span className="font-semibold">{analyticsData.engagement.weeklyActiveUsers}</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Trophy className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">Content Performance</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Playtime</span>
              {analyticsData.content.totalPlaytime === 0 ? (
                <span className="text-xs text-muted-foreground italic">No gameplay sessions yet</span>
              ) : (
                <span className="font-semibold">{analyticsData.content.totalPlaytime.toLocaleString()}m</span>
              )}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Avg Games per User</span>
              {analyticsData.content.avgGamesPerUser === 0 ? (
                <span className="text-xs text-muted-foreground italic">Waiting for first sessions</span>
              ) : (
                <span className="font-semibold">{analyticsData.content.avgGamesPerUser}</span>
              )}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Completion Rate</span>
              <span className="font-semibold">{analyticsData.content.completionRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Games Performance */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center mb-4">
          <Star className="w-5 h-5 mr-2" />
          <h3 className="text-lg font-semibold">Top Performing Games</h3>
        </div>
        <div className="space-y-3">
          {analyticsData.content.topGames.slice(0, 5).map((game, index) => (
            <div key={game.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-primary">#{index + 1}</span>
                </div>
                <div>
                  <div className="font-medium">{game.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {game.plays} plays • {game.avgRating}/5 rating
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium capitalize">{game.tier}</div>
                <div className="text-xs text-muted-foreground">Tier</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue & Retention */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center mb-4">
            <DollarSign className="w-5 h-5 mr-2" />
            <h3 className="text-lg font-semibold">Revenue Metrics</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Monthly Recurring Revenue</span>
              <span className="font-semibold">${analyticsData.revenue.mrr.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Average Revenue Per User</span>
              <span className="font-semibold">${analyticsData.revenue.arpu.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Churn Rate</span>
              <span className="font-semibold">{analyticsData.revenue.churnRate}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Customer Lifetime Value</span>
              <span className="font-semibold">${analyticsData.revenue.ltv.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {cohortData && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Calendar className="w-5 h-5 mr-2" />
              <h3 className="text-lg font-semibold">User Retention</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Day 1 Retention</span>
                <span className="font-semibold">{cohortData.retentionRates.day1}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Day 7 Retention</span>
                <span className="font-semibold">{cohortData.retentionRates.day7}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Day 30 Retention</span>
                <span className="font-semibold">{cohortData.retentionRates.day30}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Day 90 Retention</span>
                <span className="font-semibold">{cohortData.retentionRates.day90}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Performance & System Health */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center mb-4">
          <BarChart3 className="w-5 h-5 mr-2" />
          <h3 className="text-lg font-semibold">System Performance</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{analyticsData.performance.avgLoadTime}s</div>
            <div className="text-sm text-muted-foreground">Avg Load Time</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{analyticsData.performance.errorRate}%</div>
            <div className="text-sm text-muted-foreground">Error Rate</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{analyticsData.performance.uptime}%</div>
            <div className="text-sm text-muted-foreground">Uptime</div>
          </div>
          <div className="text-center p-4 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-primary">{analyticsData.performance.supportTickets}</div>
            <div className="text-sm text-muted-foreground">Support Tickets</div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Advanced Analytics Available</h3>
          <p className="text-muted-foreground mb-4">
            Get deeper insights with session recordings, heatmaps, and user journey analysis in PostHog
          </p>
          <a
            href="https://app.posthog.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open PostHog Dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

export default GamingAnalyticsDashboard;
